"use client";

import { memo, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { YouTubeEmbed } from "@/components/embeds/youtube-embed";
import { sanitizeHtml } from "@/lib/sanitize-html";
import { extractYouTubeId } from "@/lib/utils/oembed";

interface SafeHtmlRendererProps {
	html: string;
	className?: string;
	typography?: "interface" | "reading";
}

function SafeHtmlRendererComponent({
	html,
	className = "",
	typography = "interface",
}: SafeHtmlRendererProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [youtubeNodes, setYoutubeNodes] = useState<
		{ node: Element; url: string }[]
	>([]);

	// Memoize sanitization to avoid re-running on every render
	const sanitizedHtml = useMemo(() => {
		if (!html) return "";
		return sanitizeHtml(html);
	}, [html]);

	// Process oEmbed nodes after rendering to inject their HTML
	useEffect(() => {
		if (!containerRef.current || !sanitizedHtml) return;

		// Find all oEmbed nodes
		const oembedNodes = containerRef.current.querySelectorAll(
			'div[data-type="oembed"]',
		);

		const nextYoutubeNodes: { node: Element; url: string }[] = [];
		oembedNodes.forEach((node) => {
			const url = node.getAttribute("data-url");
			if (url && extractYouTubeId(url)) {
				node.innerHTML = "";
				nextYoutubeNodes.push({ node, url });
				return;
			}
			const htmlAttr = node.getAttribute("data-html");
			if (htmlAttr) {
				// Inject the embed HTML
				node.innerHTML = htmlAttr;
			}
		});
		setYoutubeNodes(nextYoutubeNodes);
	}, [sanitizedHtml]);

	if (!html || !sanitizedHtml) {
		return null;
	}

	return (
		<>
			<div
				ref={containerRef}
				style={{
					fontFamily:
						"var(--font-helvetica-sans), Helvetica, Arial, sans-serif",
				}}
				className={`prose ${typography === "reading" ? "prose-base" : "prose-sm"} dark:prose-invert min-w-0 w-full max-w-full wrap-anywhere [&_img]:max-w-full [&_img]:h-auto [&_img]:py-2 [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_code]:wrap-break-word [&_iframe]:max-w-full [&_video]:max-w-full ${className}`}
				// biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized by sanitizeHtml before rendering.
				dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
			/>
			{youtubeNodes.map(({ node, url }, index) =>
				createPortal(<YouTubeEmbed url={url} />, node, `${url}-${index}`),
			)}
		</>
	);
}

// Memoize component to prevent re-renders when props haven't changed
export const SafeHtmlRenderer = memo(SafeHtmlRendererComponent);
